import { FetchNextPageOptions, useInfiniteQuery } from '@tanstack/react-query'
import { flatten, isNil } from 'ramda'
import { useParams } from 'react-router-dom'

import { Observation, ObservationsResult } from '../../types/observation'
import { get } from '../../utils/api'
import { getOffsetFromUrl, getQueryString } from '../../utils/queryString'

const itemsPerPage = 20

export const OBSERVATIONS_PARAMS_NO_IMAGE_DATA = 'no_image_data'

export type ObservationsParams = {
  productId?: number
  trainingId?: number
  trainingTestId?: number
  availableForTesting?: number
  annotationClasses?: string
  productClasses?: string[]
  mode?: typeof OBSERVATIONS_PARAMS_NO_IMAGE_DATA
  limit?: number
  offset?: number
  dataset?: string[]
  datasetExclude?: boolean
  filename?: string
  order?: string[]
  direction?: 'asc' | 'desc'
  orderingOverride?: string
  training_id_filter?: number
}

interface RouteParams {
  trainingId: string
}

export const useObservations = (params: ObservationsParams) => {
  const { trainingId } = useParams<RouteParams>()
  const response = useInfiniteQuery(
    ['observations', params],
    ({ pageParam }) => {
      const queryString = getQueryString({
        offset: pageParam,
        ...getObservationsQueryParams(params, trainingId),
      })
      return get<ObservationsResult>(`/observations/?${queryString}`)
    },
    {
      getNextPageParam: (lastPage) => getOffsetFromUrl(lastPage.next),
      retry: false,
      keepPreviousData: true,
    }
  )

  const data = mergePages(response.data?.pages)

  const fetchNextPage = (options?: FetchNextPageOptions | undefined) =>
    !response.isFetching && response.fetchNextPage(options)

  return {
    ...response,
    data,
    fetchNextPage,
  }
}

const isObservationResult = (
  result: ObservationsResult | undefined
): result is ObservationsResult => !isNil(result)

/** Merge infinite pages results */
const mergePages = (
  pages: ObservationsResult[] | undefined
): Observation[] | undefined => {
  if (!pages) return undefined
  return flatten(pages.filter(isObservationResult).map((page) => page.results))
}

/** Transform given params in params that /observations endpoint can understand */
export const getObservationsQueryParams = (
  params: ObservationsParams,
  trainingId?: string
) => ({
  product: params.productId,
  training: params.trainingId,
  trainingTest: params.trainingTestId,
  availableForTesting: params.availableForTesting,
  annotationClasses: params.annotationClasses,
  productClasses:
    params.productClasses && params.productClasses.length
      ? params.productClasses.join()
      : undefined,
  mode: params.mode,
  limit: params.limit ? params.limit : itemsPerPage,
  dataset:
    params.dataset && params.dataset.length ? params.dataset.join() : undefined,
  dataset_exclude: params.datasetExclude,
  filename: params.filename,
  ordering:
    params.orderingOverride ??
    (params.direction === 'desc'
      ? `${params.order?.[0]},-${params.order?.[1]}`
      : params.order),
  training_id_filter: trainingId,
})
